import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Input,
  Option,
  Spinner,
  Text,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  HOLIDAY_HOURS_PER_DAY,
  holidayPaidDays,
  validateCompanyHoliday,
  type CompanyHoliday,
  type CompanyHolidayInput,
  type HolidayIssue,
  type HolidayPay,
  type HolidayPeriodPay,
} from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { MOBILE, SHRINKABLE } from '../../responsive';
import { useTimeAdminApi } from '../api';
import { toErrorMessage } from '../errors';
import { useIsAdmin } from '../useIsAdmin';

import { useColumnFilters, type FilterableColumn } from './useColumnFilters';

/** A row being created or edited. `id` is present only when editing. */
interface Draft {
  mode: 'new' | 'edit';
  id?: string;
  form: CompanyHolidayInput;
}

const EMPTY_FORM: CompanyHolidayInput = {
  name: '',
  date: '',
  note: null,
};

const useStyles = makeStyles({
  root: {
    display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalM,
    ...SHRINKABLE,
  },
  toolbar: {
    // A row of buttons plus a filter dropdown cannot fit 390px. Wrapping keeps every
    // control reachable instead of pushing the last ones off-screen.
    [MOBILE]: {
      flexWrap: 'wrap',
      rowGap: tokens.spacingVerticalS,
    },
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  spacer: { flexGrow: 1 },
  scroller: {
    // A data table stays TABULAR on a phone and scrolls sideways INSIDE this box —
    // columns like date/hours/status are only meaningful next to each other. The
    // SHRINKABLE part is what makes that work: without minWidth:0 this box refuses to
    // become narrower than the table and the page scrolls instead.
    ...SHRINKABLE,
    WebkitOverflowScrolling: 'touch',
    maxHeight: '62vh',
    overflowY: 'auto',
    overflowX: 'auto',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  // Native <table>, same as the PTO surface: cells size to their content instead
  // of clipping it, and sticky heads need borderCollapse: separate.
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: tokens.fontSizeBase300,
  },
  headCell: {
    [MOBILE]: {
      paddingLeft: tokens.spacingHorizontalXS,
      paddingRight: tokens.spacingHorizontalXS,
      fontSize: tokens.fontSizeBase200,
    },
    textAlign: 'left',
    verticalAlign: 'bottom',
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground1,
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    zIndex: 3,
  },
  /**
   * The per-column filter strip is a whole extra row of dropdowns. Useful with a
   * mouse and a wide table; on a phone it is a screenful of controls above the
   * data you came to read (Michael, 2026-09-10). Hidden below the breakpoint —
   * the report-level pickers above the table still narrow the set.
   */
  filterRow: {
    [MOBILE]: { display: 'none' },
  },
  filterCell: {
    paddingTop: 0,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    position: 'sticky',
    top: '30px',
    zIndex: 2,
  },
  cell: {
    [MOBILE]: {
      paddingLeft: tokens.spacingHorizontalXS,
      paddingRight: tokens.spacingHorizontalXS,
      fontSize: tokens.fontSizeBase200,
    },
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke3,
    verticalAlign: 'top',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    lineHeight: tokens.lineHeightBase300,
  },
  cellNoWrap: { whiteSpace: 'nowrap' },
  nameCell: { fontWeight: tokens.fontWeightSemibold },
  flagCell: { minWidth: '240px', maxWidth: '400px', color: tokens.colorPaletteRedForeground1 },
  dataRow: {
    cursor: 'pointer',
    '&:hover td': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  selectedRow: {
    '& td': { backgroundColor: tokens.colorNeutralBackground1Selected },
    '&:hover td': { backgroundColor: tokens.colorNeutralBackground1Selected },
  },
  draftRow: { '& td': { backgroundColor: tokens.colorNeutralBackground3 } },
  past: { color: tokens.colorNeutralForeground3 },
  upcoming: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorStatusWarningForeground1 },
  empty: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  error: { color: tokens.colorPaletteRedForeground1 },
  hint: { color: tokens.colorNeutralForeground3 },
  dateInput: {
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase300,
    padding: '4px 6px',
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
  },
  // The payroll period banner — what the week being worked actually owes.
  periodBanner: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  periodLabel: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  periodMuted: { color: tokens.colorNeutralForeground3 },
  payGrid: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalS },
  payRow: { display: 'flex', justifyContent: 'space-between', gap: tokens.spacingHorizontalL },
  payList: { maxHeight: '40vh', overflowY: 'auto' },
  money: { fontVariantNumeric: 'tabular-nums' },
});

type Styles = ReturnType<typeof useStyles>;

/** Local calendar date as ISO `YYYY-MM-DD`. */
function todayIso(): string {
  return new Date().toLocaleDateString('en-CA');
}

const money = (value: number): string =>
  value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

/**
 * The paid company holiday calendar — ADMIN ONLY.
 *
 * Unlike PTO this is not one row per employee: a holiday is a single
 * company-wide fact, and everyone carrying a HOLIDAY earning in NetSuite is paid
 * from it. "Who gets paid" opens the derived list for a holiday, showing each
 * eligible employee at their own rate x {@link HOLIDAY_HOURS_PER_DAY} hours per
 * paid day, and flags anyone whose earning has no rate in NetSuite rather than
 * pricing them at zero.
 */
export function HolidayReportTable(): JSX.Element {
  const styles = useStyles();
  const isAdmin = useIsAdmin();
  const api = useTimeAdminApi();

  const [holidays, setHolidays] = useState<readonly CompanyHoliday[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [issues, setIssues] = useState<readonly HolidayIssue[]>([]);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pay, setPay] = useState<{ holiday: CompanyHoliday; pay: HolidayPay } | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  /** What the payroll period being worked owes in holiday pay. */
  const [periodPay, setPeriodPay] = useState<HolidayPeriodPay | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setHolidays(await api.listHolidays());
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await api.listHolidays();
        if (!cancelled) {
          setHolidays(list);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(toErrorMessage(err));
        }
      }
      try {
        // The period summary is supplementary — a failure here must not stop the
        // calendar itself from rendering.
        const summary = await api.getHolidayPeriodPay();
        if (!cancelled) {
          setPeriodPay(summary);
        }
      } catch {
        // Leave the banner off.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const today = todayIso();

  const columns: ReadonlyArray<FilterableColumn<CompanyHoliday>> = useMemo(
    () => [
      { columnId: 'name', header: 'Holiday', filterValue: (r) => r.name },
      { columnId: 'date', header: 'Date', filterValue: (r) => r.date },
      {
        columnId: 'year',
        header: 'Year',
        filterValue: (r) => r.date.slice(0, 4),
      },
      {
        columnId: 'status',
        header: 'Status',
        filterValue: (r) => (r.date < today ? 'Past' : 'Upcoming'),
      },
    ],
    [today]
  );

  const { selections, options, setSelection, clearAll, filtered, activeCount } = useColumnFilters(
    holidays ?? [],
    columns
  );

  const validate = useCallback(
    (form: CompanyHolidayInput, editingId: string | undefined): HolidayIssue[] =>
      validateCompanyHoliday(form, {
        existing: (holidays ?? []).map((h) => ({
          id: h.id,
          name: h.name,
          date: h.date,
        })),
        ...(editingId !== undefined ? { editingId } : {}),
      }),
    [holidays]
  );

  const startNew = useCallback(() => {
    setSelectedId(null);
    setIssues([]);
    setActionError(null);
    setDraft({ mode: 'new', form: { ...EMPTY_FORM } });
  }, []);

  const startEdit = useCallback(() => {
    const row = holidays?.find((h) => h.id === selectedId);
    if (row === undefined) {
      return;
    }
    setIssues([]);
    setActionError(null);
    setDraft({
      mode: 'edit',
      id: row.id,
      form: { name: row.name, date: row.date, note: row.note },
    });
  }, [holidays, selectedId]);

  const submit = useCallback(async (): Promise<void> => {
    if (draft === null) {
      return;
    }
    const found = validate(draft.form, draft.id);
    setIssues(found);
    if (found.length > 0) {
      return;
    }
    setSaving(true);
    setActionError(null);
    try {
      if (draft.mode === 'new') {
        await api.createHoliday(draft.form);
      } else {
        await api.updateHoliday(draft.id as string, draft.form);
      }
      await reload();
      setDraft(null);
      setIssues([]);
    } catch (err) {
      setActionError(toErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }, [api, draft, reload, validate]);

  const doDelete = useCallback(async (): Promise<void> => {
    if (selectedId === null) {
      return;
    }
    setSaving(true);
    setActionError(null);
    try {
      await api.deleteHoliday(selectedId);
      await reload();
      setSelectedId(null);
    } catch (err) {
      setActionError(toErrorMessage(err));
    } finally {
      setSaving(false);
      setConfirmDelete(false);
    }
  }, [api, reload, selectedId]);

  const showPay = useCallback(async (): Promise<void> => {
    if (selectedId === null) {
      return;
    }
    setPayLoading(true);
    setActionError(null);
    try {
      setPay(await api.getHolidayPay(selectedId));
    } catch (err) {
      setActionError(toErrorMessage(err));
    } finally {
      setPayLoading(false);
    }
  }, [api, selectedId]);

  const patchForm = useCallback((patch: Partial<CompanyHolidayInput>) => {
    setDraft((prev) => (prev === null ? prev : { ...prev, form: { ...prev.form, ...patch } }));
  }, []);

  if (loadError !== null) {
    return (
      <div className={styles.empty} role="alert">
        <Text className={styles.error}>{loadError}</Text>
      </div>
    );
  }
  if (holidays === null) {
    return (
      <div className={styles.empty} role="status">
        <Spinner size="tiny" label="Loading holidays…" />
      </div>
    );
  }

  const draftFlag = issues.map((i) => i.message).join(' ');
  const selectedRow = holidays.find((h) => h.id === selectedId) ?? null;

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        {/* Editing is ADMIN ONLY (Michael, 2026-09-13) — supervisors may view the
            calendar but not change it. The API enforces this independently; these
            are hidden so a supervisor is never offered a button that 403s.
            "Who gets paid" stays: it is a read, and it is the reason a supervisor
            wants this screen. */}
        {isAdmin ? (
          <>
            <Button appearance="primary" onClick={startNew} disabled={draft !== null || saving}>
              New holiday
            </Button>
            <Button onClick={() => void submit()} disabled={draft === null || saving}>
              {saving ? <Spinner size="tiny" /> : 'Save'}
            </Button>
            <Button onClick={startEdit} disabled={selectedRow === null || draft !== null || saving}>
              Edit
            </Button>
            <Button
              onClick={() => setConfirmDelete(true)}
              disabled={selectedRow === null || draft !== null || saving}
            >
              Delete
            </Button>
          </>
        ) : null}
        <Button
          onClick={() => void showPay()}
          disabled={selectedRow === null || draft !== null || payLoading}
        >
          {payLoading ? <Spinner size="tiny" /> : 'Who gets paid'}
        </Button>
        {draft !== null ? (
          <Button
            appearance="subtle"
            onClick={() => {
              setDraft(null);
              setIssues([]);
            }}
            disabled={saving}
          >
            Cancel
          </Button>
        ) : null}
        <div className={styles.spacer} />
        <Text className={styles.hint}>
          {filtered.length} of {holidays.length} {holidays.length === 1 ? 'holiday' : 'holidays'}
        </Text>
        {activeCount > 0 ? (
          <Button appearance="subtle" size="small" onClick={clearAll}>
            Clear filters
          </Button>
        ) : null}
      </div>

      {actionError !== null ? (
        <div role="alert">
          <Text className={styles.error}>{actionError}</Text>
        </div>
      ) : null}

      {/* The payroll period being worked, and what it owes. Payroll runs a WEEKLY
          Wednesday-to-Tuesday cycle; the server decides the period from the
          payroll timezone so it cannot differ by who is looking. */}
      {periodPay !== null ? (
        <div className={styles.periodBanner}>
          <span className={styles.periodLabel}>
            Payroll period {periodPay.period.startDate} – {periodPay.period.endDate}
          </span>
          {periodPay.holidays.length === 0 ? (
            <span className={styles.periodMuted}>No holidays in this period.</span>
          ) : (
            <>
              <span>
                {periodPay.holidays.map((h) => h.holiday.name).join(', ')}
              </span>
              <span className={styles.periodLabel}>{money(periodPay.totalAmount)}</span>
              {periodPay.flaggedCount > 0 ? (
                <span className={styles.error}>
                  {periodPay.flaggedCount} employee
                  {periodPay.flaggedCount === 1 ? '' : 's'} without a rate, not included
                </span>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      <div className={styles.scroller}>
        <table className={styles.table} aria-label="Company holidays">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.columnId} className={styles.headCell} scope="col">
                  {col.header}
                </th>
              ))}
              <th className={styles.headCell} scope="col">
                Flag
              </th>
            </tr>
            <tr className={styles.filterRow}>
              {columns.map((col) => {
                const selected = selections[col.columnId] ?? [];
                const available = options[col.columnId] ?? [];
                return (
                  <td key={col.columnId} className={styles.filterCell}>
                    <FilterDropdown
                      label={col.header}
                      selected={selected}
                      available={available}
                      onChange={(values) => setSelection(col.columnId, values)}
                    />
                  </td>
                );
              })}
              <td className={styles.filterCell} />
            </tr>
          </thead>
          <tbody>
            {draft !== null && draft.mode === 'new' ? (
              <tr className={styles.draftRow}>
                <DraftCells
                  form={draft.form}
                  styles={styles}
                  patchForm={patchForm}
                  draftFlag={draftFlag}
                />
              </tr>
            ) : null}

            {filtered.map((row) => {
              const editingThis = draft?.mode === 'edit' && draft.id === row.id;
              if (editingThis && draft !== null) {
                return (
                  <tr key={row.id} className={styles.draftRow}>
                    <DraftCells
                      form={draft.form}
                      styles={styles}
                      patchForm={patchForm}
                      draftFlag={draftFlag}
                    />
                  </tr>
                );
              }
              const isPast = row.date < today;
              const days = holidayPaidDays(row).length;
              return (
                <tr
                  key={row.id}
                  className={mergeClasses(
                    styles.dataRow,
                    row.id === selectedId ? styles.selectedRow : undefined
                  )}
                  onClick={() => {
                    if (draft === null) {
                      setSelectedId(row.id === selectedId ? null : row.id);
                    }
                  }}
                  aria-selected={row.id === selectedId}
                >
                  <td className={mergeClasses(styles.cell, styles.nameCell)}>{row.name}</td>
                  <td className={mergeClasses(styles.cell, styles.cellNoWrap)}>{row.date}</td>
                  <td className={mergeClasses(styles.cell, styles.cellNoWrap)}>
                    {row.date.slice(0, 4)}
                  </td>
                  <td className={mergeClasses(styles.cell, styles.cellNoWrap)}>
                    <span className={isPast ? styles.past : styles.upcoming}>
                      {isPast ? 'Past' : 'Upcoming'}
                    </span>
                  </td>
                  <td className={mergeClasses(styles.cell, styles.flagCell)}>
                    {days === 0
                      ? 'This holiday falls entirely on a weekend, so it pays nothing.'
                      : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && draft === null ? (
          <div className={styles.empty}>
            <Text>
              {holidays.length === 0
                ? 'No holidays on the calendar. Choose "New holiday" to add one.'
                : 'No holidays match the current filters.'}
            </Text>
          </div>
        ) : null}
      </div>

      <Text className={styles.hint}>
        Every employee with a holiday earning on their NetSuite record is paid{' '}
        {HOLIDAY_HOURS_PER_DAY} hours at their own rate for that earning, per work day the holiday
        covers. Administrators only.
      </Text>

      <Dialog open={confirmDelete} onOpenChange={(_, d) => setConfirmDelete(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete this holiday?</DialogTitle>
            <DialogContent>
              {selectedRow !== null ? (
                <Text>
                  {selectedRow.name} — {selectedRow.date}
                  . Nobody will be paid for it. This cannot be undone.
                </Text>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={() => void doDelete()} disabled={saving}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={pay !== null} onOpenChange={(_, d) => (d.open ? null : setPay(null))}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{pay === null ? '' : `Who gets paid — ${pay.holiday.name}`}</DialogTitle>
            <DialogContent>
              {pay === null ? null : (
                <div className={styles.payGrid}>
                  <Text>
                    {pay.pay.paidDays.length} paid work day
                    {pay.pay.paidDays.length === 1 ? '' : 's'} · {pay.pay.hoursPerEmployee} hours per
                    employee · {pay.pay.payableCount} employee
                    {pay.pay.payableCount === 1 ? '' : 's'} ·{' '}
                    <strong className={styles.money}>{money(pay.pay.totalAmount)}</strong> total
                  </Text>
                  {pay.pay.flaggedCount > 0 ? (
                    <Text className={styles.error}>
                      {pay.pay.flaggedCount} employee
                      {pay.pay.flaggedCount === 1 ? ' has' : 's have'} no rate on their holiday
                      earning in NetSuite and {pay.pay.flaggedCount === 1 ? 'is' : 'are'} not
                      included in the total.
                    </Text>
                  ) : null}
                  <div className={styles.payList}>
                    {pay.pay.lines.map((line) => (
                      <div key={line.employeeId} className={styles.payRow}>
                        <Text>{line.employeeName}</Text>
                        <Text className={line.amount === null ? styles.error : styles.money}>
                          {line.amount === null
                            ? 'no rate'
                            : `${String(line.rate)} × ${String(line.hours)}h = ${money(line.amount)}`}
                        </Text>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setPay(null)}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

/**
 * Multi-select filter for one column — identical behaviour to the PTO table:
 * only the values actually present are offered, and they match exactly.
 */
function FilterDropdown({
  label,
  selected,
  available,
  onChange,
}: {
  label: string;
  selected: readonly string[];
  available: readonly string[];
  onChange: (values: readonly string[]) => void;
}): JSX.Element {
  return (
    <Dropdown
      size="small"
      multiselect
      placeholder="All"
      aria-label={`Filter by ${label}`}
      value={selected.length === 0 ? '' : selected.join(', ')}
      selectedOptions={[...selected]}
      onOptionSelect={(_, d) => onChange(d.selectedOptions)}
      style={{ minWidth: '110px', width: '100%' }}
    >
      {available.map((value) => (
        <Option key={value} value={value} text={value}>
          {value}
        </Option>
      ))}
    </Dropdown>
  );
}

/** The editable cells shared by New and Edit. */
function DraftCells({
  form,
  styles,
  patchForm,
  draftFlag,
}: {
  form: CompanyHolidayInput;
  styles: Styles;
  patchForm: (patch: Partial<CompanyHolidayInput>) => void;
  draftFlag: string;
}): JSX.Element {
  return (
    <>
      <td className={styles.cell}>
        <Input
          size="small"
          aria-label="Holiday name"
          placeholder="Holiday name"
          value={form.name}
          onChange={(_, d) => patchForm({ name: d.value })}
        />
      </td>
      <td className={mergeClasses(styles.cell, styles.cellNoWrap)}>
        <input
          type="date"
          className={styles.dateInput}
          aria-label="Date"
          value={form.date}
          onChange={(e) => patchForm({ date: e.target.value })}
        />
      </td>
      <td className={mergeClasses(styles.cell, styles.cellNoWrap)}>
        {form.date === '' ? '' : form.date.slice(0, 4)}
      </td>
      <td className={mergeClasses(styles.cell, styles.cellNoWrap)}>New</td>
      <td className={mergeClasses(styles.cell, styles.flagCell)}>
        {draftFlag === '' ? null : <Text className={styles.error}>{draftFlag}</Text>}
      </td>
    </>
  );
}
